import { Instagram, Facebook, Mail, Phone, MapPin } from "lucide-react";

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="footer"
      className="bg-[#060606] border-t border-[#5A4A1A]/30 pt-16 pb-8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <svg viewBox="0 0 40 28" className="w-7 h-5" fill="none">
                <path d="M2,26 L2,18 L9,5 L16,14 L20,2 L24,14 L31,5 L38,18 L38,26 Z"
                  stroke="#C9A84C" strokeWidth="1.5" strokeLinejoin="round" fill="none" />
                <line x1="2" y1="26" x2="38" y2="26" stroke="#C9A84C" strokeWidth="1.5" />
                <circle cx="20" cy="2" r="1.5" fill="#C9A84C" />
                <circle cx="9" cy="5" r="1.2" fill="#C9A84C" />
                <circle cx="31" cy="5" r="1.2" fill="#C9A84C" />
              </svg>
              <span className="font-cormorant text-base tracking-[0.2em] text-[#F0F0F0]">
                ROYAL TATTOO
              </span>
            </div>
            <p className="text-[#888888] text-xs font-outfit leading-relaxed mb-4">
              Premium Tattoo &amp; Piercing Studio mit zwei Standorten in Grevenbroich und Leverkusen.
              6× ausgezeichnet bei der Tattoo Convention Meckenheim 2025.
            </p>
            <div className="flex gap-4">
              <a href="https://instagram.com/royal.tattoo.grevenbroich" target="_blank" rel="noopener noreferrer" className="text-[#888888] hover:text-[#C9A84C] transition-colors" aria-label="Instagram Grevenbroich">
                <Instagram size={18} />
              </a>
              <a href="https://instagram.com/royal.tattoo.leverkusen" target="_blank" rel="noopener noreferrer" className="text-[#888888] hover:text-[#C9A84C] transition-colors" aria-label="Instagram Leverkusen">
                <Instagram size={18} />
              </a>
              <a href="https://instagram.com/royal.tattoo.downtown" target="_blank" rel="noopener noreferrer" className="text-[#888888] hover:text-[#C9A84C] transition-colors" aria-label="Instagram Franco">
                <Instagram size={18} />
              </a>
              <a href="https://facebook.com/RoyalTattooDowntown" target="_blank" rel="noopener noreferrer" className="text-[#888888] hover:text-[#C9A84C] transition-colors" aria-label="Facebook">
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-[#C9A84C] text-xs tracking-[0.25em] uppercase font-outfit mb-5">Navigation</h4>
            <ul className="space-y-3">
              {[
                ["Studios", "#studios"],
                ["Portfolio", "#portfolio"],
                ["Convention", "#convention"],
                ["Team", "#team"],
                ["Preise", "#preise"],
                ["Gutscheine", "#gutscheine"],
                ["Bewerbung", "#bewerbung"],
                ["Kontakt", "#kontakt"],
              ].map(([label, href]) => (
                <li key={href}>
                  <a href={href} className="text-[#888888] text-xs font-outfit hover:text-[#C9A84C] transition-colors tracking-wide">
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Grevenbroich */}
          <div>
            <h4 className="text-[#C9A84C] text-xs tracking-[0.25em] uppercase font-outfit mb-5">
              Grevenbroich
            </h4>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <MapPin size={13} className="text-[#C9A84C] mt-0.5 flex-shrink-0" />
                <p className="text-[#888888] text-xs font-outfit leading-relaxed">
                  Kölner Straße 39<br />41515 Grevenbroich
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={13} className="text-[#C9A84C] flex-shrink-0" />
                <a href="tel:01784685450" className="text-[#888888] text-xs font-outfit hover:text-[#C9A84C] transition-colors">
                  0178 4685450
                </a>
              </div>
              <p className="text-[#888888] text-xs font-outfit leading-relaxed pl-5">
                Mo–Fr 11:00–19:00<br />Sa 11:00–17:00
              </p>
              <a href="https://instagram.com/royal.tattoo.grevenbroich" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#888888] text-xs font-outfit hover:text-[#C9A84C] transition-colors">
                <Instagram size={13} />
                @royal.tattoo.grevenbroich
              </a>
            </div>
          </div>

          {/* Leverkusen */}
          <div>
            <h4 className="text-[#C9A84C] text-xs tracking-[0.25em] uppercase font-outfit mb-5">
              Leverkusen <span className="text-[#C9A84C]/60">(Mai 2026)</span>
            </h4>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <MapPin size={13} className="text-[#C9A84C] mt-0.5 flex-shrink-0" />
                <p className="text-[#888888] text-xs font-outfit leading-relaxed">
                  Kölner Straße 28<br />51379 Leverkusen (Opladen)
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={13} className="text-[#C9A84C] flex-shrink-0" />
                <a href="tel:017666124487" className="text-[#888888] text-xs font-outfit hover:text-[#C9A84C] transition-colors">
                  0176 66124487
                </a>
              </div>
              <p className="text-[#888888] text-xs font-outfit pl-5">
                Öffnungszeiten folgen
              </p>
              <a href="https://instagram.com/royal.tattoo.leverkusen" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#888888] text-xs font-outfit hover:text-[#C9A84C] transition-colors">
                <Instagram size={13} />
                @royal.tattoo.leverkusen
              </a>
              <div className="flex items-center gap-2">
                <Mail size={13} className="text-[#C9A84C] flex-shrink-0" />
                <a href="mailto:royal.tattoo.downtown@gmail.com" className="text-[#888888] text-xs font-outfit hover:text-[#C9A84C] transition-colors break-all">
                  royal.tattoo.downtown@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#5A4A1A]/20 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[#888888] text-xs font-outfit">
            &copy; {year} Royal Tattoo · Francesco Chiaromonte. Alle Rechte vorbehalten.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-[#888888] text-xs font-outfit hover:text-[#C9A84C] transition-colors">
              Datenschutz
            </a>
            <a href="#" className="text-[#888888] text-xs font-outfit hover:text-[#C9A84C] transition-colors">
              Impressum
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
